import React from 'react';
import { Callout, Step, DocHeading, DocTable, Shot } from './components';

function PageAutoCombat({ navigate }) {
  return (
    <div>
      <h1 className="text-[2.5rem] leading-[1.1] font-semibold text-white tracking-[-0.02em] mb-3">Auto Combat</h1>
      <p className="text-gray-400 leading-relaxed mb-8">
        Auto Combat uses your own skills for you. You tell it which skills to use and what kind
        each one is &mdash; <strong className="text-white">Buff</strong>,{' '}
        <strong className="text-white">Break</strong> or <strong className="text-white">Attack</strong>{' '}
        &mdash; and the type decides the order: buffs first, then breaks, then attacks, and your
        basic attack fills every gap. Aiming, range and cooldowns come from the game. Everything
        is on the <strong className="text-white">AUTOPILOT &rsaquo; Combat</strong> tab.
      </p>

      <Callout type="info">
        <strong>Nothing happens until you set it up.</strong> The skill list starts empty, and
        combat only runs while the switch in the header of the <strong>WHEN TO FIGHT</strong> card
        is on. That switch is remembered. While it is on, a green dot shows next to{' '}
        <strong>Combat</strong> in the list on the left. Combat works with a route running or
        without one, and a ready-made run on the Play tab will not start until combat is ready.
      </Callout>

      <p className="text-sm text-gray-400 mb-4">
        The tab has one wide table on top, <strong className="text-white">SKILLS</strong>, and two
        cards under it: <strong className="text-white">WHEN TO FIGHT</strong> (the main switch and
        what to attack) and <strong className="text-white">SKILL PROFILES</strong> (named copies of
        your setup).
      </p>

      <DocHeading>Set It Up in Five Minutes</DocHeading>
      <Step n={1} title="Pick your basic attack">
        The first row of the table is <strong>Basic attack</strong>. Press the swap button at the
        end of the row and choose the skill to use when nothing else is due &mdash; usually your
        left-click attack. It only offers skills that do not move you.
      </Step>
      <Step n={2} title="Pick your jump">
        The second row is <strong>Jump</strong>. Choose the skill that moves you (a leap, a charge,
        a rocket pack). The bot only jumps when something really blocks the way, toward a far kill
        target, or when the movement asks for it. If you never want it to jump, pick{' '}
        <strong>No jump at all</strong>.
      </Step>
      <Step n={3} title="Add your other skills">
        Press <strong>+ ADD A SKILL</strong>. The list shows every skill your character knows that
        is not in the table yet. A skill does not have to be on your action bar. The bot suggests a
        type for each new skill from the game&rsquo;s own data &mdash; check it.
      </Step>
      <Step n={4} title="Set the type of each skill">
        Press <strong>Buff</strong>, <strong>Break</strong> or <strong>Attack</strong> on the row.
        The table sorts itself by type &mdash; there are no up and down arrows. See the table below
        for what each type means.
      </Step>
      <Step n={5} title="Decide what to fight and switch it on">
        In <strong>WHEN TO FIGHT</strong>, pick a <strong>Fight</strong> option, then turn on the
        switch in the card header.
      </Step>

      <Shot
        name="auto-combat-01-combat-tab.png"
        tab="AUTOPILOT › Combat"
        caption="A Steam Mechanicus in a fight: the Break row is open, Heavy Shot is firing, the buff and the rocket are on cooldown."
        shoot="menushot (stage combat open): Dwarf-skillek (Quick Shot, Rocket Pack, Steam Conductor, Heavy Shot, C14 Micro-Rocket, Tesla Turret), a Heavy Shot sora lenyitva, WHEN TO FIGHT BE „Everything nearby”, SKILL PROFILES három mentett profillal."
      />

      <DocHeading>The Three Types</DocHeading>
      <DocTable
        headers={['Type', 'When it fires', 'Good for']}
        rows={[
          [<strong className="text-emerald-400">Buff</strong>, 'First of all. Cast again when its effect runs out (the bot measures it; if it cannot, it waits for the own cooldown, or 15 seconds).', 'Self buffs, auras, anything you keep up.'],
          [<strong className="text-amber-300">Break</strong>, 'After the buffs, before the attacks. The shield button next to it makes it wait until the target’s shield is broken.', 'Elemental breaks and shield breakers.'],
          [<strong className="text-brand-300">Attack</strong>, 'When no buff or break is due.', 'Everything else, turrets and placed skills included.'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        <strong className="text-white">Skills of the same type take turns:</strong> the one that
        fired longest ago goes next, so no skill can hog its type. Your{' '}
        <strong className="text-white">basic attack</strong> only fires when no other skill is
        ready. The bot recognises turrets and other placed skills from the game data and keeps
        count of them itself &mdash; they are not a separate type.
      </p>
      <p className="text-sm text-gray-400 mb-4">
        A skill that moves you (other than the one in the Jump row) says{' '}
        <em>Moves you - not the jump</em>: it is used as an attack and never to travel.
      </p>

      <DocHeading>The Columns</DocHeading>
      <DocTable
        headers={['Column', 'What it is']}
        rows={[
          [<strong className="text-white">SKILL</strong>, 'Icon and name, and under it what the game says about it: kind (ranged, melee, on you, placed on the ground...), range and cooldown.'],
          [<strong className="text-white">TYPE</strong>, 'Buff, Break or Attack. On a Break row, the shield button switches "only when the shield is down" on and off.'],
          [<strong className="text-white">OWN COOLDOWN</strong>, 'The shortest gap between two casts of this skill, set with the - and + buttons or by clicking the number. It never fires faster than the game allows anyway; raise it to hold a skill back.'],
          [<strong className="text-white">NOW</strong>, 'What the skill is doing this moment. See the next table.'],
          [<strong className="text-white">Buttons</strong>, 'The arrow opens the row. The cross removes the skill. On the Basic attack and Jump rows the swap button picks another skill.'],
        ]}
      />

      <DocHeading>The NOW Column</DocHeading>
      <DocTable
        headers={['Word', 'Meaning']}
        rows={[
          [<span className="text-emerald-400">firing</span>, 'This is the skill going out now.'],
          [<span className="text-emerald-400">ready</span>, 'Nothing stops it.'],
          [<span className="text-gray-400">waiting its turn</span>, 'Ready, but another skill of the same type (or a higher type) goes first.'],
          [<span className="text-amber-300">cooldown</span>, 'The game cooldown or your own cooldown is still running.'],
          [<span className="text-brand-300">4.2 m short</span>, 'The best skill does not reach yet - the bot walks this much closer, then fires.'],
          [<span className="text-amber-300">no target / out of range / shield up / too soon ...</span>, 'The reason it does not fire right now.'],
          [<span className="text-amber-300">the character does not know this skill</span>, 'The skill is in your setup but not on this character (a profile from another class).'],
          [<span className="text-red-400">unreadable</span>, 'The game data for the skill could not be read - usually after a game update.'],
        ]}
      />

      <DocHeading>Opening a Row</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        Click the arrow at the end of a row. It shows one sentence about what the type does, and
        under <strong className="text-white">FROM THE GAME</strong> the range, the cooldown, where
        the skill sits on your action bar, and its id.
      </p>

      <DocHeading>When to Fight</DocHeading>
      <DocTable
        headers={['Fight', 'What it does']}
        rows={[
          [<strong className="text-white">Everything nearby</strong>, 'Attacks anything your skills can reach. How far that is comes from your own skills, with a clear line of sight - there is no separate distance to set.'],
          [<strong className="text-white">Only what blocks me</strong>, 'Stops only for what stands in its way. Fastest on timed runs.'],
          [<strong className="text-white">Do not fight</strong>, 'Runs the route and walks past everything.'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        A monster the route marks as a kill target is always fought, whichever option you pick.{' '}
        <strong className="text-white">At a waiting point, within</strong> sets how far around a
        waiting point (a blood chest point, a gathering stop) it clears while it waits.
      </p>
      <p className="text-sm text-gray-400 mb-4">
        Under <strong className="text-white">ADVANCED</strong> there are two drawing aids:{' '}
        <strong className="text-white">Show where it wants to jump</strong> (green: it jumped,
        orange: it wanted to, with the reason) and{' '}
        <strong className="text-white">Show the corridor and the pack</strong> (the walls, the
        monsters blocking the path, and where it would land behind them).
      </p>

      <DocHeading>Skill Profiles</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        Your setup is saved by itself, separately for every character &mdash; you never have to
        save it by hand. A profile is a named <em>copy</em>: make one before you try something new,
        load it back if it did not work out, or use it to carry a setup to another character.{' '}
        <strong className="text-white">Now using</strong> shows where your current setup came from.
        Settings follow the skill <em>name</em>, not the slot on the action bar.
      </p>
      <Callout type="info">
        Profiles from the old combat system are converted once when they load: a skill that waited
        for a broken shield becomes a Break, a self buff becomes a Buff, everything else an Attack,
        and the old pauses become the own cooldown.
      </Callout>

      <DocHeading>Why Doesn&rsquo;t It Fight?</DocHeading>
      <DocTable
        headers={['What you see', 'What to do']}
        rows={[
          ['"The fight is off - turn it on in the COMBAT tab."', 'Turn on the switch in the WHEN TO FIGHT header.'],
          ['"No skill is turned on - set up your skills in the COMBAT tab."', 'Add at least one skill besides the basic attack.'],
          ['"COMBAT is set to Do not fight - pick what it should attack."', 'Pick Everything nearby or Only what blocks me.'],
          ['A skill never fires, NOW says "the character does not know this skill"', 'The row belongs to another character. Remove it or load the right profile.'],
          ['It jumps into walls or never jumps', 'Check the Jump row, then turn on Show where it wants to jump - it writes the reason next to your character.'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        Running routes with it is covered on{' '}
        <button onClick={() => navigate('autopilot')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">AUTOPILOT &rsaquo; Play</button>.
      </p>
    </div>
  );
}

export default PageAutoCombat;
